import { Head, Link } from "@inertiajs/react"
import { DollarSign, Search } from "lucide-react"

import AppLayout from "@/layouts/app-layout"
import {
  employeePath,
  employeesPath,
  newEmployeePath,
  permissionsEmployeesPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface Employee {
  id: number
  name: string
  first_name: string | null
  last_name: string | null
  initials: string | null
  job_title: string | null
  email: string
  work_phone: string | null
  cell_phone: string | null
  status: string
  employment_type: number | null
  org_level: number | null
  roles: string[]
}

interface Props {
  employees: Employee[]
  group?: string | null
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Employees", href: "/employees" },
]

export default function Index({ employees, group }: Props) {
  const activeEmployees = employees.filter((e) => e.status === "active")
  const inactiveEmployees = employees.filter((e) => e.status === "inactive")

  const grouped = {
    Full: activeEmployees.filter(
      (e) => e.org_level !== null && e.org_level !== 8 && e.org_level > 0,
    ),
    Limited: activeEmployees.filter((e) => e.org_level === 8),
    None: activeEmployees.filter(
      (e) => e.org_level === null || e.org_level === 0,
    ),
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Employees" />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            {group ? `${group}` : "Employees"}
          </h1>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-sm">
              <Link
                href={employeesPath({ group: "Investigators" })}
                className={`rounded px-2 py-1 ${group === "Investigators" ? "bg-hpi-dark-blue text-white" : "text-hpi-active-blue hover:bg-hpi-pale-blue"}`}
              >
                Investigator
              </Link>
              <span className="text-hpi-med-gray">|</span>
              <Link
                href={employeesPath({ group: "Marketing" })}
                className={`rounded px-2 py-1 ${group === "Marketing" ? "bg-hpi-dark-blue text-white" : "text-hpi-active-blue hover:bg-hpi-pale-blue"}`}
              >
                Marketing
              </Link>
              {group && (
                <>
                  <span className="text-hpi-med-gray">|</span>
                  <Link
                    href={employeesPath()}
                    className="text-hpi-active-blue hover:bg-hpi-pale-blue rounded px-2 py-1"
                  >
                    All
                  </Link>
                </>
              )}
            </div>
            <Link
              href={permissionsEmployeesPath()}
              className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Permission Matrix
            </Link>
            <Link
              href={newEmployeePath()}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Add Employee
            </Link>
          </div>
        </div>

        {Object.entries(grouped).map(([level, emps]) =>
          emps.length > 0 ? (
            <div key={level} className="mb-6">
              <h2 className="text-hpi-dark-gray mb-2 text-lg font-medium">
                {level} Access ({emps.length})
              </h2>
              <EmployeeTable employees={emps} />
            </div>
          ) : null,
        )}

        {inactiveEmployees.length > 0 && (
          <details className="mt-6">
            <summary className="text-hpi-med-gray cursor-pointer text-lg font-medium">
              Inactive ({inactiveEmployees.length})
            </summary>
            <div className="mt-2">
              <EmployeeTable employees={inactiveEmployees} />
            </div>
          </details>
        )}
      </div>
    </AppLayout>
  )
}

function EmployeeTable({ employees }: { employees: Employee[] }) {
  return (
    <table className="border-hpi-light-gray w-full border-collapse border">
      <thead>
        <tr className="bg-hpi-pale-gray">
          <th className="border-hpi-light-gray border px-4 py-2 text-left">
            Name
          </th>
          <th className="border-hpi-light-gray border px-4 py-2 text-left">
            Job Title
          </th>
          <th className="border-hpi-light-gray border px-4 py-2 text-left">
            Phone
          </th>
          <th className="border-hpi-light-gray border px-4 py-2 text-left">
            Email
          </th>
          <th className="border-hpi-light-gray border px-4 py-2 text-left">
            Roles
          </th>
        </tr>
      </thead>
      <tbody>
        {employees.map((emp) => (
          <tr key={emp.id} className="hover:bg-hpi-pale-blue">
            <td className="border-hpi-light-gray border px-4 py-2">
              <Link
                href={employeePath(emp.id)}
                className="text-hpi-active-blue hover:text-hpi-active-blue"
              >
                {emp.name}
              </Link>
              {emp.roles.includes("investigator") && (
                <span title="Investigator">
                  <Search className="text-hpi-med-gray ml-1 inline h-3.5 w-3.5" />
                </span>
              )}
              {emp.roles.includes("sales") && (
                <span title="Marketing">
                  <DollarSign className="text-hpi-med-gray ml-1 inline h-3.5 w-3.5" />
                </span>
              )}
            </td>
            <td className="border-hpi-light-gray border px-4 py-2">
              {emp.job_title}
            </td>
            <td className="border-hpi-light-gray border px-4 py-2">
              {emp.work_phone ?? emp.cell_phone}
            </td>
            <td className="border-hpi-light-gray border px-4 py-2">
              {emp.email}
            </td>
            <td className="border-hpi-light-gray border px-4 py-2">
              {emp.roles.join(", ")}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
